import React from "react";
import { useDispatch } from "react-redux";
import MySignature from "./MySignature";
import OthersSignature from "./OthersSignature";
import PropTypes from "prop-types";
import { fetchSentForSignatureList } from "../../../slice/HomeSlice";
import { signatureStatusConfig } from "../../../utils/constants";
import GenericTabContainer from "../../../components/common/GenericTabContainer";
import { createHandleBellClickGeneric } from "../../../utils/commonHandlers";
import { remindSignature, cancelSignatureRequest } from "../../../slice/MatterDetailsSlice";

const SignatureTab = ({
  activeTab,
  handleTabChange,
  isLoading,
  isInitialLoad,
  mySignatureList,
  sentForSignatureList,
  getRowProps,
  setError,
  setShowNotification,
  handleError,
  setIsLoading,
  handleAPICalls,
}) => {
  const dispatch = useDispatch();

  // Sent first: on the E-Signatures page that is the question people come
  // to answer; what needs *my* signature also shows up in My Work.
  const tabs = [
    { key: "sentForSignature", label: "Sent for Signature" },
    { key: "needsMySignature", label: "Needs My Signature" },
  ];

  const handleCancelClick = async (record) => {
    if (!window.confirm(`Cancel the signature request for "${record.file_name}"?`)) return;
    setIsLoading(true);
    try {
      await dispatch(cancelSignatureRequest(record.signature_request_id)).unwrap();
      // Same toast protocol as the Remind handler: the host renders
      // `error` entries, and `success: true` draws the green tick.
      setError([{ id: Date.now().toString(), newMsg: "Signature request cancelled", success: true }]);
      setShowNotification(true);
      dispatch(fetchSentForSignatureList());
    } catch (err) {
      handleError(err, "Failed to cancel signature request");
    } finally {
      setIsLoading(false);
    }
  };

  const tabComponents = {
    needsMySignature: MySignature,
    sentForSignature: OthersSignature,
    props: {
      mySignatureList,
      sentForSignatureList,
      handleCancelClick,
    }
  };

  const bellClickConfig = {
    handleBellClick: createHandleBellClickGeneric({
      dispatch,
      remindField: 'workflow_visualization',
      refreshAction: fetchSentForSignatureList,
      setIsLoading,
      setError,
      setShowNotification,
      handleError,
      errorContext: "Signature Tab Error sending reminders",
      remindAction: remindSignature,
      perReviewer: false,
      getSignatureId: (record) => record.signature_request_id
    })
  };

  const chipCountConfig = {
    needsMySignature: (chip) => mySignatureList?.stats?.[chip] || 0,
    sentForSignature: (chip) => sentForSignatureList?.stats?.[chip] || 0
  };

  return (
    <GenericTabContainer
      activeTab={activeTab}
      handleTabChange={handleTabChange}
      isLoading={isLoading}
      isInitialLoad={isInitialLoad}
      getRowProps={getRowProps}
      setError={setError}
      setShowNotification={setShowNotification}
      handleError={handleError}
      setIsLoading={setIsLoading}
      handleAPICalls={handleAPICalls}
      statusOptions={signatureStatusConfig}
      tabs={tabs}
      tabComponents={tabComponents}
      bellClickConfig={bellClickConfig}
      chipCountConfig={chipCountConfig}
    />
  );
};

SignatureTab.propTypes = {
  activeTab: PropTypes.string.isRequired,
  handleTabChange: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isInitialLoad: PropTypes.bool,
  mySignatureList: PropTypes.object,
  sentForSignatureList: PropTypes.object,
  getRowProps: PropTypes.func.isRequired,
  setError: PropTypes.func.isRequired,
  setShowNotification: PropTypes.func.isRequired,
  handleError: PropTypes.func.isRequired,
  setIsLoading: PropTypes.func.isRequired,
  handleAPICalls: PropTypes.func.isRequired,
};

export default SignatureTab;
